import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import Layout from '../components/layout';
import SEO from '../components/seo';
import Hero from '../components/hero';
import SideBySide from '../components/side-by-side';
import IntegrationsGrid from '../components/integrations-grid';
import Carousel from '../components/carousel';
import DialogButton from '../components/dialogButton';

import Img from 'gatsby-image';
import '../styles/dispensary-point-of-sale.css';

const PointOfSale = () => {
    const data = useStaticQuery(graphql`
        query {
            hero: file(
                relativePath: { eq: "background-green-store-signs.png" }
            ) {
                childImageSharp {
                    fluid(maxWidth: 2600, jpegQuality: 80) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
            map: file(relativePath: { eq: "map.png" }) {
                childImageSharp {
                    fluid(maxWidth: 800) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
            payment: file(relativePath: { eq: "weave-payment.jpg" }) {
                childImageSharp {
                    fluid(maxWidth: 800) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
            posTrio: file(relativePath: { eq: "pos-trio.png" }) {
                childImageSharp {
                    fluid(maxWidth: 1600) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
            marketplace: file(relativePath: { eq: "marketplace.png" }) {
                childImageSharp {
                    fluid(maxWidth: 800) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
            accounting: file(relativePath: { eq: "accounting.png" }) {
                childImageSharp {
                    fluid(maxWidth: 800) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
            devices: file(relativePath: { eq: "devices.jpeg" }) {
                childImageSharp {
                    fluid(maxWidth: 800) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
        }
    `);

    return (
        <Layout>
            <SEO
                title="Dispensary Point of Sale System"
                url="/dispensary-point-of-sale"
                description="Weave is the most intuitive and reliable point of sale system for your cannabis business. Built on the Google Cloud infrastructure with 99.9% uptime and critical accuracy, you can trust every transaction is correct. Get up to speed in minutes with our simple system and training tutorials and avoid spending hours training to complete a sale."
            />
            <Hero image={data.hero.childImageSharp.fluid}>
                <div className="pos-flex-container">
                    <Img
                        className="pos-trio-image-container"
                        fluid={data.posTrio.childImageSharp.fluid}
                    />
                    <div className="pos-flex-copy-container">
                        <div>
                            <h1>Point of Sale</h1>
                            <p>
                                Weave is the most intuitive and reliable point
                                of sale system for your cannabis business. Built
                                on the Google Cloud infrastructure with 99.9%
                                uptime and critical accuracy, you can trust
                                every transaction is correct. Get up to speed in
                                minutes with our simple system and training
                                tutorials and avoid spending hours training to
                                complete a sale.
                            </p>
                        </div>
                        <div className="btns">
                            <DialogButton
                                analyticsValue={3}
                                className="cta"
                                key="talk"
                            >
                                Book demo
                            </DialogButton>
                        </div>
                    </div>
                </div>
            </Hero>
            <div className="parallax-section parallax-white">
                <SideBySide
                    bulletsList={[
                        'Define dispensary discount criteria such as time of day, day of the week, and customer group. Use this tool to trigger deals like Munchie Monday or a Night Owl Special to increase traffic during slower days or times.',
                        'Run promotions on specific brands.',
                        'Set store-wide permissions for discounts that can only be enabled by managers, or allow all of your budtenders to have the same permissions.',
                    ]}
                    buttonsList={[
                        <DialogButton key="one">Get Started</DialogButton>,
                    ]}
                    copy="Configure automatic, custom discounts based on time of day, customer group, product category, and much more."
                    gif={
                        'https://storage.googleapis.com/weave-web-images/dynamicdiscounting.gif'
                    }
                    imagePosition={'left'}
                    sideHeader="Dynamic Discounting"
                />
            </div>
            <div className="parallax-section parallax-white">
                <SideBySide
                    bulletsList={[
                        'Use permissions to determine who is authorized to approve a discount or sale.',
                        'Managers can approve restricted actions or discounts for employees by entering their PIN to allow for day-to-day flexibility.',
                        'For businesses with multiple locations and multi-state operators, set permissions specific to a location or across locations.',
                    ]}
                    copy="Grant and restrict access to dispensary employees based on their specific role at your dispensary."
                    gif={
                        'https://storage.googleapis.com/weave-web-images/permissions.gif'
                    }
                    imagePosition={'right'}
                    sideHeader="Employee Permissions"
                />
            </div>
            <div className="parallax-section parallax-white">
                <SideBySide
                    bulletsList={[
                        'Microsoft Windows and Apple computers and tablets',
                        'Receipt printers',
                        'Scanners',
                        'Product label and barcode printers',
                    ]}
                    copy="Weave works with your existing hardware so you can easily integrate our point of sale at the lowest cost to your business. Weave is compatible with all:"
                    image={data.devices.childImageSharp.fluid}
                    imagePosition={'left'}
                    sideHeader="Device Compatibility"
                />
            </div>
            <div className="parallax-section parallax-white">
                <SideBySide
                    bulletsList={[
                        'Looping and overselling limits are based on each state’s rules and regulations, which are built into Weave.',
                        'Patient records are checked among every dispensary in the Weave network to safeguard against overselling.',
                    ]}
                    buttonsList={[
                        <DialogButton key="purchase-limits">
                            Request a Demo
                        </DialogButton>,
                    ]}
                    copy="Weave automatically checks each sale against patient records – including both daily and monthly histories – to help prevent looping and overselling."
                    gif={
                        'https://storage.googleapis.com/weave-web-images/legalliimit.gif'
                    }
                    imagePosition={'right'}
                    sideHeader="Purchase Limits"
                />
            </div>
            <div className="parallax-section parallax-white">
                <SideBySide
                    bulletsList={[
                        'Scan customer IDs to automatically populate customer data.',
                        'Create customer groups to trigger discounts or special pricing.',
                        'Set up a waiting list during busy periods to manage customer arrival and track walkouts.',
                        'Create a customer loyalty program.',
                        'Track customer referrals.',
                    ]}
                    copy="Weave is built with customer management in mind to enhance day-to-day dispensary operations."
                    gif={'//storage.googleapis.com/weave-web-images/scan.gif'}
                    imagePosition={'left'}
                    sideHeader="Customer Management"
                />
            </div>
            <div className="parallax-section parallax-white">
                <SideBySide
                    bulletsList={[
                        'The audit log shows all movement of money in your dispensary.',
                        'Set up city, county, and state specific tax rates in the Weave system to know exactly how much is owed during tax season.',
                    ]}
                    copy="Know exactly how much money you’ve made, see what is in your cash drawers at any given moment, and track discrepancies."
                    image={data.accounting.childImageSharp.fluid}
                    imagePosition={'right'}
                    sideHeader="Accounting"
                />
            </div>
            <div className="parallax-section parallax-white">
                <SideBySide
                    buttonsList={[
                        <DialogButton key="one">Learn More</DialogButton>,
                    ]}
                    copy={[
                        'Weave offers a reliable and fully integrated payment processing system that works. Increase customer satisfaction and your bottom line by accepting debit cards in your dispensaries.',
                        <br></br>,
                        <br></br>,
                        <p>
                            A recent analysis shows average transaction size
                            increases by <strong>12%</strong> when customers pay
                            with their debit card. Our payment processing guide
                            clearly explains the costs and steps required to
                            take your dispensary out of the restrictions of a
                            cash-only world.
                        </p>,
                    ]}
                    image={data.payment.childImageSharp.fluid}
                    imagePosition={'left'}
                    sideHeader="Debit Card Processing"
                />
            </div>
            <div className="parallax-section parallax-white">
                <SideBySide
                    copy="Optimize your inventory and purchase order decisions with the power of Weave. Our Marketplace automatically generates purchase order suggestions and tells you which items should be discounted or offloaded. The Marketplace isn’t designed to replace the buyer, but it will significantly cut down on time spent analyzing inventory and making critical purchasing decisions."
                    image={data.marketplace.childImageSharp.fluid}
                    imagePosition={'right'}
                    sideHeader="Marketplace"
                />
            </div>
            <div className="parallax-section parallax-white">
                <IntegrationsGrid
                    header="API Integrations"
                    copy="We understand we can't do it all. By partnering with the best in the industry, we make sure you and your customers have a seamless end to end experience."
                />
            </div>
            <Carousel />
        </Layout>
    );
};

export default PointOfSale;
